import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Button } from "@/components/base/buttons/button";
import { useAuth } from "@/contexts/AuthProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";

export const Route = createFileRoute("/auth/callback")({
	component: AuthCallback,
});

/**
 * OAuth Callback Route
 *
 * This route handles the return from OAuth authentication.
 * It processes the authentication result and redirects the user
 * to their intended destination.
 */
function AuthCallback() {
	const {
		handleOAuthReturn,
		isLoading,
		isInitializing,
		isProcessingCallback,
		error,
	} = useAuth();
	const navigate = useNavigate();
	const hasRun = useRef(false);
	const { t } = useAppTranslation();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <false positive>
	useEffect(() => {
		// Prevent multiple calls by using a ref flag
		if (!hasRun.current) {
			hasRun.current = true;
			console.log("🔄 OAuth callback: Processing return...");
			handleOAuthReturn();
		}
	}, []); // ✅ Remove handleOAuthReturn dependency to prevent infinite loop

	// Show loading state if processing callback, regular loading, or initializing
	const showLoading = isProcessingCallback || isLoading || isInitializing;

	// Only show error if we have an error AND we're not loading/processing
	const showError = error && !showLoading;

	return (
		<div className="flex min-h-screen items-center justify-center bg-background-primary">
			<div className="text-center">
				{showLoading ? (
					<>
						<div className="mb-4">
							<div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent mx-auto" />
						</div>
						<h1 className="text-xl font-semibold text-gray-900">
							{t("authCallback.processingAuthentication")}
						</h1>
						<p className="text-gray-600 mt-2">{t("authCallback.pleaseWait")}</p>
					</>
				) : showError ? (
					<>
						<h1 className="text-xl font-semibold text-red-600">
							{t("authCallback.authenticationError")}
						</h1>
						<p className="text-gray-600 mt-2 mb-4">
							{error ||
								t("authCallback.somethingWentWrongDuringAuthentication")}
						</p>
						<Button
							onClick={() => {
								navigate({ to: "/login", replace: true });
							}}
							className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
						>
							{t("authCallback.returnToLogin")}
						</Button>
					</>
				) : (
					<>
						<div className="mb-4">
							<div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent mx-auto" />
						</div>
						<h1 className="text-xl font-semibold text-gray-900">
							{t("authCallback.completingAuthentication")}
						</h1>
						<p className="text-gray-600 mt-2">
							{t("authCallback.youWillBeRedirectedShortly")}
						</p>
					</>
				)}
			</div>
		</div>
	);
}
